import { useEffect, useId } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';

export interface Conversation {
  id: string;
  care_circle_id: string;
  title: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ConversationMessage {
  id: string;
  conversation_id: string;
  care_circle_id: string;
  author_id: string;
  content: string;
  mentions: string[];
  created_at: string;
}

export function useConversations(circleId: string | null) {
  const qc = useQueryClient();
  // Unique per hook instance — useConversations runs in both MessagesPage and
  // the nav unread badge, and two channels sharing a topic makes Supabase reuse
  // the already-subscribed channel, which throws on the second .on().
  const subId = useId();
  const query = useQuery<Conversation[]>({
    queryKey: ['conversations', circleId],
    enabled: !!circleId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('conversations')
        .select('id, care_circle_id, title, created_by, created_at, updated_at')
        .eq('care_circle_id', circleId!)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  useEffect(() => {
    if (!circleId) return;
    const channel = supabase
      .channel(`conversations:${circleId}:${subId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'conversations', filter: `care_circle_id=eq.${circleId}` },
        () => void qc.invalidateQueries({ queryKey: ['conversations', circleId] }),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [circleId, qc, subId]);

  return query;
}

export function useCreateConversation(circleId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (title: string) => {
      if (!circleId) throw new Error('no_circle');
      const { data: userResp } = await supabase.auth.getUser();
      const uid = userResp.user?.id;
      if (!uid) throw new Error('not_signed_in');
      const { data, error } = await supabase
        .from('conversations')
        .insert({ care_circle_id: circleId, title: title.trim() || 'New conversation', created_by: uid })
        .select('id')
        .single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversations', circleId] }),
  });
}

export function useMessages(conversationId: string | null) {
  const qc = useQueryClient();
  const subId = useId();
  const query = useQuery<ConversationMessage[]>({
    queryKey: ['messages', conversationId],
    enabled: !!conversationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('conversation_messages')
        .select('id, conversation_id, care_circle_id, author_id, content, mentions, created_at')
        .eq('conversation_id', conversationId!)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  useEffect(() => {
    if (!conversationId) return;
    const channel = supabase
      .channel(`messages:${conversationId}:${subId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversation_messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        () => void qc.invalidateQueries({ queryKey: ['messages', conversationId] }),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [conversationId, qc, subId]);

  return query;
}

export function useSendMessage(circleId: string | null, conversationId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { content: string; mentions: string[] }) => {
      if (!circleId || !conversationId) throw new Error('missing');
      const { data: userResp } = await supabase.auth.getUser();
      const uid = userResp.user?.id;
      if (!uid) throw new Error('not_signed_in');

      const { data: msg, error } = await supabase
        .from('conversation_messages')
        .insert({
          conversation_id: conversationId,
          care_circle_id: circleId,
          author_id: uid,
          content: input.content,
          mentions: input.mentions,
        })
        .select('id')
        .single();
      if (error) throw error;

      // The thread's updated_at is bumped by a DB trigger on insert.
      // Route @-mention notifications (scope-checked server-side).
      if (input.mentions.length > 0 && msg) {
        await supabase.rpc('notify_mentions', {
          p_circle: circleId,
          p_source_type: 'message',
          p_source_id: msg.id,
          p_user_ids: input.mentions,
          p_preview: input.content,
        });
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['messages', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations', circleId] });
    },
  });
}
